import React, { useEffect, useState } from "react";
import Wrapper from "../../../components/TenantWrapper";
import "./dashboard.css";
import title from "../../../components/title";
import { GetAppliedFlat } from "../../../redux/reducer/GetAppliedFlat";
import { GetTenantInterestRequests } from "../../../redux/reducer/FlatInterestRequest";
import { GetTenantViolations } from "../../../redux/reducer/AgreementViolation";
import { GetAllTenantMaintenanceRequest } from "../../../redux/reducer/GetAllTenantMaintenanceRequest";
import { usePropertyRule } from "../../../utils/propertyRules";
import { RulesBadge } from "../../../components/PropertyRulesPanel";
import RentalDetailDialog from "./RentalDetailDialog";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Skeleton,
  Avatar,
  Chip,
  LinearProgress,
  Paper,
  Divider,
  Stack,
} from "@mui/material";
import {
  Home as HomeIcon,
  Search as SearchIcon,
  Description as AgreementIcon,
  Receipt as BillIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Cancel as RejectIcon,
  ArrowForward as ArrowIcon,
  Apartment as ApartmentIcon,
  Send as SendIcon,
  Build as MaintenanceIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  HourglassEmpty as WaitingIcon,
  Celebration as CelebrationIcon,
  Payments as PaymentsIcon,
  AutoAwesome as SparkleIcon,
  Gavel as GavelIcon,
  ErrorOutline as AlertIcon,
} from "@mui/icons-material";

export default function TenantDashboard() {
  title("Dashboard");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.sidebar.open);
  const { theme: t } = useTheme();

  const { validateUser } = useSelector((state) => state);
  const userId = validateUser?.UserValidate?.data?.user?._id;
  const userName =
    validateUser?.UserValidate?.data?.user?.tenantName ||
    validateUser?.UserValidate?.data?.user?.name ||
    "Tenant";

  const [loading, setLoading] = useState(true);
  const [appliedFlats, setAppliedFlats] = useState([]);
  const [interestRequests, setInterestRequests] = useState([]);
  const [violations, setViolations] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [recordLoading, setRecordLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  useEffect(() => {
    if (userId) {
      setLoading(true);
      Promise.all([
        dispatch(GetAppliedFlat({ userId })),
        dispatch(GetTenantInterestRequests({ tenantId: userId })),
      ]).then(([appliedRes, requestsRes]) => {
        setAppliedFlats(appliedRes?.payload?.data?.data || []);
        setInterestRequests(requestsRes?.payload?.data?.data || []);
        setLoading(false);
      });
    }
  }, [dispatch, userId]);

  // Safe arrays
  const safeAppliedFlats = Array.isArray(appliedFlats) ? appliedFlats : [];
  const safeInterestRequests = Array.isArray(interestRequests)
    ? interestRequests
    : [];

  // A flat only counts as an active rental once the payment is verified *and*
  // the owner has confirmed the move-in actually happened. A verified payment
  // on its own is a booking — the tenant may still be weeks from moving in.
  //
  // Bookings made before move-in confirmation existed have no moveInStatus at
  // all; those tenants are already live and must not be demoted by this check.
  const verifiedRequests = safeInterestRequests.filter(
    (r) =>
      r.paymentStatus === "verified" &&
      r.moveInStatus !== "awaiting" &&
      r.moveInStatus !== "cancelled"
  );

  // Paid and booked, still waiting to move in. Surfaced separately so the
  // dashboard says "moving in on the 15th" rather than pretending they live
  // there already.
  const awaitingMoveIn = safeInterestRequests.filter(
    (r) => r.moveInStatus === "awaiting"
  );

  // Calculate stats
  const stats = {
    totalApplications: safeInterestRequests.length,
    activeRentals: verifiedRequests.length,
    pendingRequests: safeInterestRequests.filter((r) => r.status === "pending")
      .length,
    rejectedRequests: safeInterestRequests.filter(
      (r) => r.status === "rejected" || r.status === "payment_rejected"
    ).length,
  };

  // Get active rental (first payment-verified request), enriched with the
  // richer property/landlord details from the applied-flat record when available
  const activeRentalRequest = verifiedRequests[0];
  const activeRentalEnrichment = activeRentalRequest
    ? safeAppliedFlats.find(
        (f) => f?.propertyDetail?.[0]?._id === activeRentalRequest.flatId
      )
    : null;
  const activeRentalProperty = activeRentalEnrichment?.propertyDetail?.[0];
  const activeRentalLandlord = activeRentalEnrichment?.landlordDetail;

  const activeRental = activeRentalRequest
    ? {
        flatName: activeRentalRequest.flatName || activeRentalProperty?.flatName,
        flatRent: activeRentalRequest.flatRent ?? activeRentalProperty?.flatRent,
        image:
          activeRentalRequest.flatDetail?.images?.[0] ||
          activeRentalProperty?.images?.[0],
        mapAddress: activeRentalProperty?.mapAddress,
        landlordId: activeRentalRequest.landlordId,
        flatId: activeRentalRequest.flatId,
        landlordName:
          activeRentalRequest.landlordName || activeRentalLandlord?.ownerName,
        landlordEmail: activeRentalLandlord?.email,
        landlordPhone: activeRentalLandlord?.phoneNumber,
      }
    : null;

  // Violations and maintenance are keyed on the flat the tenant actually
  // rents, so they can only be fetched once the active rental has resolved.
  const activeFlatId = activeRental?.flatId;
  const activeLandlordId = activeRental?.landlordId;

  useEffect(() => {
    if (!userId || !activeFlatId) {
      setViolations([]);
      setMaintenance([]);
      return;
    }

    let cancelled = false;
    setRecordLoading(true);

    Promise.all([
      dispatch(GetTenantViolations({ tenantId: userId })),
      dispatch(
        GetAllTenantMaintenanceRequest({ tenantId: userId, flatId: activeFlatId })
      ),
    ]).then(([violationRes, maintenanceRes]) => {
      if (cancelled) return;

      // GetTenantViolations spans every property the tenant has ever rented;
      // the card is about this flat, so anything else is filtered out here.
      const allViolations = violationRes?.payload?.data?.data || [];
      setViolations(
        (Array.isArray(allViolations) ? allViolations : []).filter((v) => {
          const propertyId = v?.propertyId?._id || v?.propertyId;
          return !propertyId || String(propertyId) === String(activeFlatId);
        })
      );

      const requests = maintenanceRes?.payload?.data || [];
      setMaintenance(Array.isArray(requests) ? requests : []);
      setRecordLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, userId, activeFlatId]);

  const { rules, loading: rulesLoading } = usePropertyRule(
    activeLandlordId,
    activeFlatId
  );

  const openViolations = violations.filter((v) => v.status !== "RESOLVED");
  const openMaintenance = maintenance.filter((m) =>
    ["pending", "accepted", "in-progress"].includes(
      String(m.status || "").toLowerCase()
    )
  );

  // Check if tenant is new (no applications)
  const isNewTenant = safeInterestRequests.length === 0;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const gradient = `linear-gradient(120deg, ${t.primary} 0%, ${t.secondary} 100%)`;

  // Onboarding steps
  const onboardingSteps = [
    {
      step: 1,
      title: "Search Properties",
      description:
        "Browse available properties that match your requirements and budget.",
      icon: <SearchIcon sx={{ fontSize: 32 }} />,
      completed: false,
      action: () => navigate("/find-property"),
      actionLabel: "Find Property",
    },
    {
      step: 2,
      title: "Send Interest Request",
      description:
        "Found a property you like? Send an interest request to the landlord.",
      icon: <SendIcon sx={{ fontSize: 32 }} />,
      completed: safeInterestRequests.length > 0,
      action: () => navigate("/find-property"),
      actionLabel: "Browse Properties",
    },
    {
      step: 3,
      title: "Wait for Approval",
      description:
        "The landlord will review your request and respond within a few days.",
      icon: <WaitingIcon sx={{ fontSize: 32 }} />,
      completed: stats.activeRentals > 0 || stats.rejectedRequests > 0,
      action: () => navigate("/my-requests"),
      actionLabel: "View Requests",
    },
    {
      step: 4,
      title: "Move In",
      description:
        "Once approved, sign the agreement and move into your new home!",
      icon: <CelebrationIcon sx={{ fontSize: 32 }} />,
      completed: stats.activeRentals > 0,
      action: null,
      actionLabel: null,
    },
  ];

  const currentStep =
    onboardingSteps.findIndex((s) => !s.completed) + 1 ||
    onboardingSteps.length;
  const completedCount = onboardingSteps.filter((s) => s.completed).length;

  /* ------------------------------------------------------------------ */
  /* Shared building blocks                                              */
  /* ------------------------------------------------------------------ */

  const cardSx = {
    borderRadius: "20px",
    border: "1px solid rgba(18,32,58,0.07)",
    boxShadow: "0 12px 30px -20px rgba(18,32,58,0.5)",
    background: "#fff",
  };

  const heroBanner = (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "24px",
        p: { xs: 2.5, sm: 3.5, md: 4 },
        mb: 3,
        color: "#fff",
        background: gradient,
      }}
    >
      {/* decorative orbs */}
      <Box
        sx={{
          position: "absolute",
          width: 260,
          height: 260,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.13)",
          top: -120,
          right: -60,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 170,
          height: 170,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.09)",
          bottom: -90,
          right: 120,
          display: { xs: "none", sm: "block" },
        }}
      />

      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Chip
            icon={<SparkleIcon sx={{ fontSize: 15, color: "#fff !important" }} />}
            label={greeting}
            size="small"
            sx={{
              mb: 1.5,
              color: "#fff",
              fontWeight: 600,
              bgcolor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(6px)",
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              lineHeight: 1.15,
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.4rem" },
            }}
          >
            {userName}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              opacity: 0.92,
              maxWidth: 520,
              fontSize: { xs: "0.88rem", md: "1rem" },
            }}
          >
            {activeRental
              ? `You are renting ${activeRental.flatName}. Everything you need is one tap away.`
              : "Let's find you a home you'll love — browse, request, and move in."}
          </Typography>

          <Stack
            direction="row"
            spacing={1.2}
            sx={{ mt: 2.5, flexWrap: "wrap", gap: 1.2 }}
          >
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => navigate("/find-property")}
              sx={{
                bgcolor: "#fff",
                color: t.primary,
                fontWeight: 700,
                borderRadius: "999px",
                px: 2.5,
                textTransform: "none",
                boxShadow: "0 10px 24px -12px rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
            >
              Find Property
            </Button>
            {activeRental && (
              <Button
                variant="outlined"
                startIcon={<PaymentsIcon />}
                onClick={() => navigate("/rent-payment")}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.6)",
                  fontWeight: 600,
                  borderRadius: "999px",
                  px: 2.5,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#fff",
                    bgcolor: "rgba(255,255,255,0.14)",
                  },
                }}
              >
                Pay Rent
              </Button>
            )}
          </Stack>
        </Box>

        {activeRental && (
          <Box
            sx={{
              position: "relative",
              p: 2,
              minWidth: { xs: "100%", md: 230 },
              borderRadius: "18px",
              bgcolor: "rgba(255,255,255,0.16)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
              Monthly Rent
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.6rem" }}>
              PKR {Number(activeRental.flatRent || 0).toLocaleString()}
            </Typography>
            <Divider sx={{ my: 1.2, borderColor: "rgba(255,255,255,0.28)" }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <CheckIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 12.5 }}>Payment verified</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );

  const StatCard = ({ label, value, icon, tone, onClick }) => (
    <Card
      onClick={onClick}
      sx={{
        ...cardSx,
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
        transition:
          "transform .28s cubic-bezier(.34,1.56,.64,1), box-shadow .28s ease",
        "&:hover": onClick
          ? {
              transform: "translateY(-6px)",
              boxShadow: "0 22px 40px -22px rgba(18,32,58,0.6)",
            }
          : {},
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${tone} 0%, ${tone}55 100%)`,
        }}
      />
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              width: 46,
              height: 46,
              borderRadius: "14px",
              bgcolor: `${tone}1f`,
              color: tone,
            }}
          >
            {icon}
          </Avatar>
          {onClick && (
            <ArrowIcon sx={{ fontSize: 18, color: "text.disabled" }} />
          )}
        </Box>
        <Typography
          sx={{ fontWeight: 800, fontSize: "1.9rem", lineHeight: 1.1 }}
        >
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  const statsRow = (
    <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ mb: 3 }}>
      <Grid item xs={6} md={3}>
        <StatCard
          label="Active Rentals"
          value={stats.activeRentals}
          icon={<HomeIcon />}
          tone="#16a34a"
          onClick={() => navigate("/my-requests")}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          label="Pending Requests"
          value={stats.pendingRequests}
          icon={<PendingIcon />}
          tone="#f59e0b"
          onClick={() => navigate("/my-requests")}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          label="Applications"
          value={stats.totalApplications}
          icon={<ApartmentIcon />}
          tone={t.primary}
          onClick={() => navigate("/my-requests")}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          label="Rejected"
          value={stats.rejectedRequests}
          icon={<RejectIcon />}
          tone="#ef4444"
        />
      </Grid>
    </Grid>
  );

  const QuickAction = ({ icon, label, onClick, tone }) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.6,
        borderRadius: "16px",
        cursor: "pointer",
        border: "1px solid rgba(18,32,58,0.08)",
        transition: "all .25s ease",
        "&:hover": {
          borderColor: tone,
          bgcolor: `${tone}0d`,
          transform: "translateX(4px)",
        },
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 38,
          height: 38,
          borderRadius: "12px",
          bgcolor: `${tone}1f`,
          color: tone,
          "& svg": { fontSize: 19 },
        }}
      >
        {icon}
      </Avatar>
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{label}</Typography>
      <ArrowIcon sx={{ ml: "auto", fontSize: 17, color: "text.disabled" }} />
    </Box>
  );

  const quickActionsPanel = (
    <Paper
      elevation={0}
      sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, height: "100%" }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 2 }}>
        Quick Actions
      </Typography>
      <Stack spacing={1.2}>
        <QuickAction
          icon={<SearchIcon />}
          label="Find New Property"
          tone={t.primary}
          onClick={() => navigate("/find-property")}
        />
        <QuickAction
          icon={<SendIcon />}
          label="My Requests"
          tone="#3b82f6"
          onClick={() => navigate("/my-requests")}
        />
        {activeRental && (
          <>
            <QuickAction
              icon={<PaymentsIcon />}
              label="Pay Rent"
              tone="#16a34a"
              onClick={() => navigate("/rent-payment")}
            />
            <QuickAction
              icon={<BillIcon />}
              label="Current Bills"
              tone="#f59e0b"
              onClick={() =>
                navigate(
                  `/current-Bill/${activeRental.landlordId}/${activeRental.flatId}`
                )
              }
            />
            <QuickAction
              icon={<AgreementIcon />}
              label="View Agreement"
              tone="#8b5cf6"
              onClick={() =>
                navigate(
                  `/tenant-agreement/${activeRental.landlordId}/${activeRental.flatId}`
                )
              }
            />
            <QuickAction
              icon={<MaintenanceIcon />}
              label="Maintenance Request"
              tone="#ec4899"
              onClick={() =>
                navigate(
                  `/tenant-maintenance-request/${activeRental.landlordId}/${activeRental.flatId}`
                )
              }
            />
          </>
        )}
      </Stack>
    </Paper>
  );

  const RequestCard = ({ request }) => {
    const isVerified = request.paymentStatus === "verified";
    const isRejected =
      request.status === "rejected" || request.status === "payment_rejected";
    const tone = isVerified ? "#16a34a" : isRejected ? "#ef4444" : "#f59e0b";

    // A booked flat is not a rented one. Opening the tenancy pages before the
    // owner has confirmed the move-in shows an agreement, a rent bill and a
    // move-out button for a tenancy that has not started; the booking is
    // managed on My Requests until then.
    const canOpenFlat = isVerified && request.moveInStatus !== "awaiting";

    return (
      <Card
        onClick={() =>
          canOpenFlat
            ? navigate(
                `/tenant-functionality/${request.landlordId}/${request.flatId}`
              )
            : request.moveInStatus === "awaiting" && navigate("/my-requests")
        }
        sx={{
          ...cardSx,
          height: "100%",
          cursor:
            canOpenFlat || request.moveInStatus === "awaiting"
              ? "pointer"
              : "default",
          opacity: isRejected ? 0.75 : 1,
          borderLeft: `4px solid ${tone}`,
          transition: "transform .25s ease, box-shadow .25s ease",
          "&:hover": isVerified
            ? {
                transform: "translateY(-4px)",
                boxShadow: "0 20px 36px -22px rgba(18,32,58,0.55)",
              }
            : {},
        }}
      >
        <CardContent sx={{ p: 2.2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
              mb: 1.2,
            }}
          >
            <Typography
              noWrap
              sx={{ fontWeight: 700, fontSize: 15, maxWidth: "62%" }}
            >
              {request.flatName}
            </Typography>
            <Chip
              size="small"
              label={
                isVerified
                  ? "Active"
                  : isRejected
                  ? "Rejected"
                  : request.paymentStatus === "verifying"
                  ? "Verifying"
                  : "Pending"
              }
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                color: tone,
                bgcolor: `${tone}1a`,
              }}
            />
          </Box>

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1.5 }}
          >
            <PersonIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {request.landlordName}
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: 18, color: t.primary }}>
            PKR {Number(request.flatRent || 0).toLocaleString()}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              /month
            </Typography>
          </Typography>

          {!isVerified && !isRejected && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                borderRadius: "10px",
                bgcolor: "#fff7e6",
                display: "flex",
                alignItems: "center",
                gap: 0.8,
              }}
            >
              <WaitingIcon sx={{ fontSize: 15, color: "#b45309" }} />
              <Typography variant="caption" sx={{ color: "#b45309" }}>
                {request.status === "pending"
                  ? "Waiting for landlord approval"
                  : request.paymentStatus === "verifying"
                  ? "Waiting for payment verification"
                  : "Payment required to activate"}
              </Typography>
            </Box>
          )}

          {isVerified && (
            <Button
              fullWidth
              size="small"
              endIcon={<ArrowIcon />}
              sx={{
                mt: 1.8,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                color: "#fff",
                background: gradient,
              }}
            >
              View Details
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const requestsSection = (heading, items) => (
    <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
          {heading}
        </Typography>
        <Button
          size="small"
          endIcon={<ArrowIcon />}
          onClick={() => navigate("/my-requests")}
          sx={{ textTransform: "none", color: t.primary, fontWeight: 600 }}
        >
          View All
        </Button>
      </Box>
      <Grid container spacing={2}>
        {items.map((request, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={request._id || request.id || index}
          >
            <RequestCard request={request} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  /* ------------------------------------------------------------------ */
  /* Onboarding view (new tenant)                                        */
  /* ------------------------------------------------------------------ */
  const renderOnboarding = () => (
    <Box sx={{ py: 1 }}>
      {heroBanner}

      {/* Booked, not yet moved in. Without this the tenant lands on the
          onboarding view with no sign that they have already paid and secured
          a property. */}
      {awaitingMoveIn.map((request) => (
        <Paper
          key={request._id || request.flatId}
          elevation={0}
          sx={{
            ...cardSx,
            mb: 3,
            p: { xs: 2, md: 2.5 },
            border: "1px solid #bfdbfe",
            background:
              "linear-gradient(120deg, rgba(59,130,246,0.10) 0%, rgba(59,130,246,0.02) 100%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
            <CheckIcon sx={{ color: "#1d4ed8" }} />
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
              {request.flatName} is booked for you
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Move-in date: <strong>{request.moveInDate || "not set"}</strong>. Your
            owner confirms once you are in — bills, maintenance and rent payment
            unlock at that point.
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowIcon />}
            onClick={() => navigate("/my-requests")}
            sx={{
              mt: 1.4,
              textTransform: "none",
              fontWeight: 700,
              color: t.primary,
            }}
          >
            View booking
          </Button>
        </Paper>
      ))}

      {/* Journey progress */}
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
              Your Journey
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {completedCount} of {onboardingSteps.length} steps complete
            </Typography>
          </Box>
          <Chip
            label={`Step ${currentStep} of ${onboardingSteps.length}`}
            size="small"
            sx={{ fontWeight: 700, color: "#fff", background: gradient }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={(completedCount / onboardingSteps.length) * 100}
          sx={{
            height: 10,
            borderRadius: 999,
            bgcolor: "#eef0f5",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              background: gradient,
            },
          }}
        />
      </Paper>

      {/* Steps */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
        {onboardingSteps.map((step) => {
          const isCurrent = currentStep === step.step;
          const tone = step.completed ? "#16a34a" : t.primary;
          return (
            <Grid item xs={12} sm={6} md={3} key={step.step}>
              <Card
                sx={{
                  ...cardSx,
                  height: "100%",
                  position: "relative",
                  opacity: step.step > currentStep ? 0.62 : 1,
                  border: isCurrent
                    ? `2px solid ${t.primary}`
                    : "1px solid rgba(18,32,58,0.07)",
                  transition: "transform .28s ease, box-shadow .28s ease",
                  "&:hover": step.action
                    ? {
                        transform: "translateY(-6px)",
                        boxShadow: "0 22px 40px -22px rgba(18,32,58,0.55)",
                      }
                    : {},
                }}
              >
                <CardContent sx={{ p: 2.5, textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 62,
                      height: 62,
                      mx: "auto",
                      mb: 1.6,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: step.completed ? "#fff" : tone,
                      background: step.completed ? "#16a34a" : `${tone}1a`,
                    }}
                  >
                    {step.completed ? (
                      <CheckIcon sx={{ fontSize: 32 }} />
                    ) : (
                      step.icon
                    )}
                  </Box>

                  <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.8 }}>
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, minHeight: { md: 62 }, fontSize: 13 }}
                  >
                    {step.description}
                  </Typography>

                  {step.completed ? (
                    <Chip
                      icon={<CheckIcon />}
                      label="Completed"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        color: "#16a34a",
                        bgcolor: "#16a34a1a",
                        "& .MuiChip-icon": { color: "#16a34a" },
                      }}
                    />
                  ) : step.action && isCurrent ? (
                    <Button
                      size="small"
                      endIcon={<ArrowIcon />}
                      onClick={step.action}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "999px",
                        px: 2,
                        color: "#fff",
                        background: gradient,
                      }}
                    >
                      {step.actionLabel}
                    </Button>
                  ) : (
                    <Chip
                      label={isCurrent ? "In Progress" : "Pending"}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {safeInterestRequests.length > 0 &&
        requestsSection(
          "My Property Requests",
          safeInterestRequests.slice(0, 3)
        )}

      {/* Closing CTA */}
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          mt: 3,
          p: { xs: 2.5, md: 3.5 },
          textAlign: "center",
          background: `linear-gradient(135deg, ${t.lightBg} 0%, #ffffff 100%)`,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 19, mb: 0.8 }}>
          Ready to find your home?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Browse verified properties and send interest requests to landlords.
        </Typography>
        <Button
          startIcon={<SearchIcon />}
          onClick={() => navigate("/find-property")}
          sx={{
            px: 3.5,
            py: 1.2,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 700,
            color: "#fff",
            background: gradient,
            boxShadow: `0 14px 28px -14px ${t.primary}`,
          }}
        >
          Search Properties
        </Button>
      </Paper>
    </Box>
  );

  /* ------------------------------------------------------------------ */
  /* Things that need the tenant to act                                  */
  /* ------------------------------------------------------------------ */
  // An open violation can end a tenancy, so it is read in full on the
  // dashboard itself. Burying it behind a chip, a dialog and a tab was the
  // wrong weight for something with that consequence — the dialog and the
  // report are for history, not for the thing that is happening right now.
  const attentionPanel =
    openViolations.length === 0 && openMaintenance.length === 0 ? null : (
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          mb: 3,
          overflow: "hidden",
          border: "1px solid rgba(180,83,9,0.18)",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 1.6,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            background:
              "linear-gradient(120deg, rgba(251,191,36,0.16) 0%, rgba(251,191,36,0.04) 100%)",
          }}
        >
          <AlertIcon sx={{ color: "#b45309" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
              Needs your attention
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[
                openViolations.length &&
                  `${openViolations.length} open violation${
                    openViolations.length > 1 ? "s" : ""
                  }`,
                openMaintenance.length &&
                  `${openMaintenance.length} maintenance request${
                    openMaintenance.length > 1 ? "s" : ""
                  } in progress`,
              ]
                .filter(Boolean)
                .join(" • ")}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={1.4}>
            {openViolations.slice(0, 2).map((v) => (
              <Box
                key={v._id}
                sx={{
                  p: 1.8,
                  borderRadius: "14px",
                  border: "1px solid rgba(180,83,9,0.16)",
                  bgcolor: "#fffbeb",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.8,
                    mb: 0.8,
                  }}
                >
                  <GavelIcon sx={{ fontSize: 17, color: "#b45309" }} />
                  <Typography sx={{ fontWeight: 700 }}>{v.reason}</Typography>
                  {v.severity === "HIGH" && (
                    <Chip
                      size="small"
                      label="High severity"
                      sx={{
                        fontWeight: 700,
                        fontSize: 11,
                        color: "#b91c1c",
                        bgcolor: "#fef2f2",
                      }}
                    />
                  )}
                  {v.status === "ESCALATED" && (
                    <Chip
                      size="small"
                      label="Escalated to move-out"
                      sx={{
                        fontWeight: 700,
                        fontSize: 11,
                        color: "#b91c1c",
                        bgcolor: "#fef2f2",
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: "auto" }}
                  >
                    {v.createdAt
                      ? new Date(v.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : ""}
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: "#37425c" }}>
                  {v.description}
                </Typography>

                {v.apology?.decision === "PENDING" ? (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontWeight: 600, color: "#1d4ed8" }}
                  >
                    Your response has been sent — waiting on the owner.
                  </Typography>
                ) : (
                  <Button
                    size="small"
                    onClick={() =>
                      navigate(
                        `/unregister-tenant-request/${activeRental.landlordId}/${activeRental.flatId}`
                      )
                    }
                    sx={{
                      mt: 1.2,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "999px",
                      px: 2.2,
                      color: "#fff",
                      bgcolor: "#b45309",
                      "&:hover": { bgcolor: "#92400e" },
                    }}
                  >
                    Respond
                  </Button>
                )}
              </Box>
            ))}

            {openMaintenance.slice(0, 2).map((m) => (
              <Box
                key={m._id}
                sx={{
                  p: 1.8,
                  borderRadius: "14px",
                  border: "1px solid #dbeafe",
                  bgcolor: "#eff6ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.8,
                    mb: 0.6,
                  }}
                >
                  <MaintenanceIcon sx={{ fontSize: 17, color: "#1d4ed8" }} />
                  <Typography sx={{ fontWeight: 700 }}>
                    {m.category || "Maintenance"}
                  </Typography>
                  <Chip
                    size="small"
                    label={m.status}
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: "#1d4ed8",
                      bgcolor: "#dbeafe",
                      textTransform: "capitalize",
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: "#37425c" }}>
                  {m.description}
                </Typography>
              </Box>
            ))}
          </Stack>

          {(openViolations.length > 2 || openMaintenance.length > 2) && (
            <Button
              size="small"
              endIcon={<ArrowIcon />}
              onClick={() => {
                setDetailTab(openViolations.length > 2 ? 2 : 3);
                setDetailOpen(true);
              }}
              sx={{
                mt: 1.6,
                textTransform: "none",
                fontWeight: 700,
                color: t.primary,
              }}
            >
              See everything
            </Button>
          )}
        </Box>
      </Paper>
    );

  /* ------------------------------------------------------------------ */
  /* Full dashboard (active tenant)                                      */
  /* ------------------------------------------------------------------ */
  const renderDashboard = () => (
    <Box sx={{ py: 1 }}>
      {heroBanner}
      {statsRow}
      {attentionPanel}

      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        {activeRental && (
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, height: "100%" }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 2 }}>
                Your Current Rental
              </Typography>

              <Card
                elevation={0}
                onClick={() =>
                  navigate(
                    `/tenant-functionality/${activeRental.landlordId}/${activeRental.flatId}`
                  )
                }
                sx={{
                  borderRadius: "18px",
                  border: "1px solid rgba(18,32,58,0.08)",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "box-shadow .25s ease, transform .25s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 20px 36px -22px rgba(18,32,58,0.5)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: { xs: "100%", sm: 210 },
                      height: { xs: 170, sm: "auto" },
                      minHeight: { sm: 210 },
                      bgcolor: "#eef0f5",
                      flexShrink: 0,
                    }}
                  >
                    {activeRental.image ? (
                      <Box
                        component="img"
                        src={activeRental.image}
                        alt={activeRental.flatName}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          minHeight: 170,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <HomeIcon sx={{ fontSize: 46, color: "#c3c8d4" }} />
                      </Box>
                    )}
                    <Chip
                      icon={<CheckIcon sx={{ color: "#fff !important" }} />}
                      label="Verified"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        fontWeight: 700,
                        color: "#fff",
                        bgcolor: "#16a34a",
                      }}
                    />
                  </Box>

                  <CardContent sx={{ flex: 1, p: 2.4, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                      {activeRental.flatName}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        mt: 0.6,
                      }}
                    >
                      <LocationIcon
                        sx={{ fontSize: 15, color: "text.secondary" }}
                      />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {activeRental.mapAddress || "Location not specified"}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1.8 }} />

                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      Landlord
                    </Typography>
                    <Stack spacing={0.8}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PersonIcon sx={{ fontSize: 15, color: t.primary }} />
                        <Typography variant="body2">
                          {activeRental.landlordName}
                        </Typography>
                      </Box>
                      {activeRental.landlordEmail && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <EmailIcon sx={{ fontSize: 15, color: t.primary }} />
                          <Typography variant="body2" noWrap>
                            {activeRental.landlordEmail}
                          </Typography>
                        </Box>
                      )}
                      {activeRental.landlordPhone && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <PhoneIcon sx={{ fontSize: 15, color: t.primary }} />
                          <Typography variant="body2">
                            {activeRental.landlordPhone}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Standing of the tenancy in four signals. Counts only —
                        anything that needs reading lives behind Full Details,
                        otherwise the card stops being scannable. */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.8,
                        mt: 2,
                      }}
                    >
                      {recordLoading ? (
                        <Skeleton variant="rounded" width={190} height={24} />
                      ) : (
                        <>
                          <Chip
                            size="small"
                            icon={
                              openViolations.length ? (
                                <AlertIcon sx={{ fontSize: 15 }} />
                              ) : (
                                <CheckIcon sx={{ fontSize: 15 }} />
                              )
                            }
                            label={
                              openViolations.length
                                ? `${openViolations.length} open violation${
                                    openViolations.length > 1 ? "s" : ""
                                  }`
                                : "No open violations"
                            }
                            sx={{
                              fontWeight: 700,
                              fontSize: 11.5,
                              color: openViolations.length
                                ? "#b45309"
                                : "#15803d",
                              bgcolor: openViolations.length
                                ? "#fffbeb"
                                : "#f0fdf4",
                              "& .MuiChip-icon": {
                                color: openViolations.length
                                  ? "#b45309"
                                  : "#15803d",
                              },
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<MaintenanceIcon sx={{ fontSize: 15 }} />}
                            label={
                              openMaintenance.length
                                ? `${openMaintenance.length} open request${
                                    openMaintenance.length > 1 ? "s" : ""
                                  }`
                                : `${maintenance.length} maintenance request${
                                    maintenance.length === 1 ? "" : "s"
                                  }`
                            }
                            sx={{
                              fontWeight: 700,
                              fontSize: 11.5,
                              color: openMaintenance.length
                                ? "#1d4ed8"
                                : "#475569",
                              bgcolor: openMaintenance.length
                                ? "#eff6ff"
                                : "#f1f5f9",
                              "& .MuiChip-icon": {
                                color: openMaintenance.length
                                  ? "#1d4ed8"
                                  : "#475569",
                              },
                            }}
                          />
                          <RulesBadge rules={rules} />
                        </>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                        mt: 2.2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Monthly Rent
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: 20,
                            color: t.primary,
                          }}
                        >
                          PKR{" "}
                          {Number(activeRental.flatRent || 0).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<GavelIcon />}
                          onClick={(e) => {
                            // The card itself navigates to the tenancy screen;
                            // this opens the record instead.
                            e.stopPropagation();
                            setDetailTab(0);
                            setDetailOpen(true);
                          }}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "999px",
                            px: 2,
                            color: t.primary,
                            borderColor: t.primary,
                          }}
                        >
                          Full Details
                        </Button>
                        <Button
                          size="small"
                          endIcon={<ArrowIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "999px",
                            px: 2.2,
                            color: "#fff",
                            background: gradient,
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Box>
              </Card>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={activeRental ? 4 : 12}>
          {quickActionsPanel}
        </Grid>
      </Grid>

      {safeInterestRequests.length > 1 &&
        requestsSection(
          "All Property Requests",
          safeInterestRequests.slice(0, 3)
        )}
    </Box>
  );

  /* ------------------------------------------------------------------ */
  /* Loading                                                             */
  /* ------------------------------------------------------------------ */
  if (loading) {
    return (
      <>
        <Wrapper />
        <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
          <Skeleton
            variant="rectangular"
            height={190}
            sx={{ borderRadius: "24px", mb: 3 }}
          />
          <Grid container spacing={2.5}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={6} md={3} key={i}>
                <Skeleton
                  variant="rectangular"
                  height={150}
                  sx={{ borderRadius: "20px" }}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={8}>
              <Skeleton
                variant="rectangular"
                height={300}
                sx={{ borderRadius: "20px" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton
                variant="rectangular"
                height={300}
                sx={{ borderRadius: "20px" }}
              />
            </Grid>
          </Grid>
        </div>
      </>
    );
  }

  return (
    <>
      <Wrapper />
      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        {isNewTenant || !activeRental ? renderOnboarding() : renderDashboard()}
      </div>

      <RentalDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        rental={activeRental}
        rules={rules}
        rulesLoading={rulesLoading}
        violations={violations}
        maintenance={maintenance}
        accent={t.primary}
        initialTab={detailTab}
      />
    </>
  );
}
